// The last pictures of a store's theme, which the themes screen shows in place of a live frame, as Shopify's current
// theme card shows the store as it last looked rather than a page running inside a frame. The admin opens the store
// out of sight at a desktop and a phone width with ?capture=1; the store draws its own first screen and posts the
// picture back to the admin that asked, which keeps it. Shared so both ends spell the address and message alike.

/** The two pictures of a store's home page, and when they were taken. */
export interface ThemeSnapshot {
  desktopUrl: string;
  phoneUrl: string;
  capturedAt: string;
}

/** The screens each picture is drawn at: a laptop and a phone. */
export const SNAPSHOT_SCREENS = {
  desktop: { width: 1440, height: 900 },
  phone: { width: 390, height: 844 }
} as const;

/** Which of the two pictures one is. */
export type SnapshotScreen = keyof typeof SNAPSHOT_SCREENS;

/** The type of the message a capturing store posts to the admin that opened it. */
export const CAPTURE_MESSAGE = 'xshop:capture';

/** How old the pictures may grow before the themes screen takes them again, with no theme save since. */
export const SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Reports whether a theme's pictures should be taken again: none yet, the theme saved since, or a day old.
export function snapshotStale(snapshot: ThemeSnapshot | null | undefined, savedAt: string | null | undefined, now = Date.now()): boolean {
  const taken = snapshot ? Date.parse(snapshot.capturedAt) : NaN;
  if (!Number.isFinite(taken)) return true;
  const saved = savedAt ? Date.parse(savedAt) : NaN;
  return (Number.isFinite(saved) && saved > taken) || now - taken > SNAPSHOT_MAX_AGE_MS;
}

// Builds the address that asks a store to draw its first screen and post it to the admin at that origin.
export function captureAddress(storeUrl: string, adminOrigin: string): string {
  const url = new URL(storeUrl);
  url.searchParams.set('capture', '1');
  url.searchParams.set('to', adminOrigin);
  return url.toString();
}

// Reads the admin origin a capture visit answers to, or null when the visit is not one or names no web origin.
export function captureTarget(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.get('capture') !== '1') return null;
  try {
    const to = new URL(params.get('to') ?? '');
    return to.protocol === 'https:' || to.protocol === 'http:' ? to.origin : null;
  } catch {
    return null;
  }
}
